import React from "react"
import { P, TextBlock } from "@cd2/text"
import { createSelectField } from "../../../componentFactories/_createSelectField"
import { IRecord } from "../../../types/schema"
import { buildSearchFilter } from "../../../utils/buildSearchFilter"

export type IUser = IRecord<
  "skeem_users",
  "name" | "skeem_email" | "payrollNumber" | "firstName" | "lastName"
>

const commonProps = {
  model: "skeem_users",
  attributes: ["name", "skeem_email", "payrollNumber", "firstName", "lastName"],
  getSkeemSort() {
    return [
      { by: "lastName", direction: "asc" as const },
      { by: "firstName", direction: "asc" as const },
    ]
  },
  renderOption(record: IUser) {
    return (
      <TextBlock blockSpacing={0}>
        <P>{record.name}</P>
        <P size="small">{record.payrollNumber || record.skeem_email}</P>
      </TextBlock>
    )
  },
}

function baseFilter(
  query: string,
  allowSelf?: boolean,
  allUsers?: boolean,
  scopeToSessionCompany?: boolean,
) {
  return {
    and: [
      {
        matches: {
          archived: false,
          isAnonymous: false,
          isLeaver: false,
        },
      },
      allowSelf ? null : { matches: { id: { not: { session: true } } } },
      scopeToSessionCompany !== false
        ? {
            exists: {
              path: ["company", { name: "users", filter: { matches: { id: { session: true } } } }],
            },
          }
        : null,
      {
        or: [
          { matches: { firstName: { like: query } } },
          { matches: { lastName: { like: query } } },
          { matches: { skeem_email: { like: query } } },
          { matches: { contactEmail: { like: query } } },
          { matches: { payrollNumber: { like: query } } },
        ],
      },
      allUsers
        ? null
        : {
            or: [
              { matches: { teamsNotifiedFrom: { empty: false } } },
              { matches: { companiesNotifiedFrom: { empty: false } } },
            ],
          },
    ].filter(Boolean),
  }
}

export const AllUserSelect = createSelectField<IUser>({
  ...commonProps,
  queryToSkeemFilter(query) {
    return baseFilter(query, true, true)
  },
})

export const UserSelect = createSelectField<IUser>({
  ...commonProps,
  queryToSkeemFilter(query) {
    return baseFilter(query, true)
  },
})

/** Approvers only (team or company leadersToNotify). Not scoped to session company so approvers in other sub-companies can be selected. Pass skeemFilter to scope to organisation (e.g. accessibleCompanyIds). */
export const ChooseReviewerUserSelect = createSelectField<IUser>({
  ...commonProps,
  queryToSkeemFilter(query) {
    return baseFilter(query, false, false, false)
  },
})

export const ForwardToUserSelectField = createSelectField<IUser>({
  ...commonProps,
  queryToSkeemFilter(query) {
    return baseFilter(query)
  },
})
export const ForwardToUserSelectFieldIncSelf = createSelectField<IUser>({
  ...commonProps,
  queryToSkeemFilter(query) {
    return baseFilter(query, true)
  },
})

/** Users in given companies (by accessibleCompanies). Use .Multi with skeemFilter for companyIds. */
function companyUsersBaseFilter(query: string) {
  return {
    and: [
      { matches: { archived: false, isAnonymous: false, isLeaver: false } },
      buildSearchFilter(
        ["firstName", "lastName", "skeem_email", "contactEmail", "payrollNumber"],
        query,
      ),
    ],
  }
}

export const CompanyUsersSelect = createSelectField<IUser>({
  ...commonProps,
  queryToSkeemFilter: companyUsersBaseFilter,
})
